"use client";

import { useEffect, useState } from "react";

import { PillNav } from "@/components/nav/pill-nav";
import { NodePair } from "@/components/v2/node-pair";
import { RasterField } from "@/components/v2/raster-field";
import { submitBetaRequest } from "@/lib/api/beta";
import landingTextsFallback from "@/config/landing-texts.json";

import "../landing.css";

/* ══════════════════════════════════════════════════════════════
   Ecosystem reports — coming soon.

   The landing page promises "ecosystem data and reports" (service card 04,
   the ticker and the footer tagline). Until the first edition ships, this is
   where that promise lands: what the report is, that it is in build, and a
   waitlist that reuses the beta request endpoint with `source: "reports"`.

   Copy comes from the same admin-editable landing texts as the landing page.
══════════════════════════════════════════════════════════════ */

/* What the first edition covers — the same database read at market level. */
const COVERAGE = [
  {
    n: "01",
    color: "var(--al-blue)",
    title: "Who is hiring what",
    desc: "Demand across the Czech scene by stack, seniority and company, read from the same profiles Alister ranks.",
  },
  {
    n: "02",
    color: "var(--al-amber)",
    title: "Which stacks are heating up",
    desc: "Movement in languages, frameworks and tooling — measured on real code and real career moves, not job-ad keywords.",
  },
  {
    n: "03",
    color: "var(--al-red)",
    title: "How the scene moves",
    desc: "Quarter-by-quarter direction of travel: where engineers go, which companies grow teams, where the ecosystem thickens.",
  },
];

function renderMarkup(text: string): React.ReactNode[] {
  return text.split("\n").flatMap((line, li, lines) => {
    const parts = line.split(/(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*)/g);
    const nodes = parts.map((part, pi) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return <span key={`${li}-${pi}`} className="al-hl">{part.slice(2, -2)}</span>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={`${li}-${pi}`}>{part.slice(1, -1)}</em>;
      }
      return <span key={`${li}-${pi}`}>{part}</span>;
    });
    if (li < lines.length - 1) nodes.push(<br key={`br-${li}`} />);
    return nodes;
  });
}

function ArrowIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ReportsContent() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = (key: string) => landingTextsFallback[key as keyof typeof landingTextsFallback] ?? "";

  const submit = async () => {
    if (!email.trim() || !name.trim() || !company.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await submitBetaRequest({
        name: name.trim(),
        company: company.trim(),
        email: email.trim(),
        need: interest.trim(),
        source: "reports",
      });
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="al-v2 al-landing">
      <RasterField />
      <PillNav variant="landing" />

      <section className="al-page-hero">
        <div
          className="al-orn"
          style={{ right: "-140px", bottom: "-60px", width: "min(46vw,520px)", height: "min(46vw,520px)", animation: "al-drift 18s ease-in-out infinite" }}
        >
          <NodePair fill="#DDD8CE" />
        </div>
        <div
          className="al-orn"
          style={{ right: "12vw", top: "16vh", width: "min(20vw,220px)", height: "min(20vw,220px)", borderRadius: "50%", background: "var(--al-amber)", mixBlendMode: "multiply" }}
        />

        <div className="al-wrap">
          <span style={{ display: "inline-flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <span className="al-kicker al-kicker-blue">
              <span className="al-kicker-dot" />
              {t("reports_eyebrow")}
            </span>
            <span className="al-kicker al-kicker-outline">{t("reports_status")}</span>
          </span>

          <h1>{renderMarkup(t("reports_title"))}</h1>
          <p className="al-lead">{renderMarkup(t("reports_lead"))}</p>

          <div className="al-reports">
            <div className="al-reports-list">
              {COVERAGE.map((c) => (
                <div key={c.n} className="al-card al-reports-item">
                  <span className="al-num" style={{ background: c.color }}>{c.n}</span>
                  <div className="t">{c.title}</div>
                  <div className="b">{c.desc}</div>
                </div>
              ))}
              <p className="al-reports-note">
                Beta testers see the first edition before anyone else —{" "}
                <a href="/#beta">request beta access</a>.
              </p>
            </div>

            <div className="al-card al-beta-card al-reports-card">
              {!submitted ? (
                <>
                  <span className="k">{t("reports_form_title")}</span>
                  <p className="al-reports-sub">{t("reports_form_sub")}</p>
                  <div className="al-beta-form" style={{ borderTop: "none", paddingTop: 0, marginTop: "22px" }}>
                    <div className="al-beta-row">
                      <input
                        type="text"
                        className="al-beta-input"
                        placeholder={t("beta_name_placeholder")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoComplete="name"
                        required
                      />
                      <input
                        type="text"
                        className="al-beta-input"
                        placeholder={t("beta_company_placeholder")}
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        autoComplete="organization"
                        required
                      />
                    </div>
                    <input
                      type="email"
                      className="al-beta-input"
                      placeholder={t("beta_email_placeholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                    <textarea
                      className="al-beta-textarea"
                      placeholder="What would you want to read in it?"
                      rows={2}
                      value={interest}
                      onChange={(e) => setInterest(e.target.value)}
                    />
                    <button
                      type="button"
                      className="al-btn al-btn-beta"
                      onClick={submit}
                      disabled={sending || !name.trim() || !company.trim() || !email.trim()}
                    >
                      {sending ? "Sending…" : t("reports_btn_label")}
                      {!sending && <ArrowIcon />}
                    </button>
                    {error && <p className="al-beta-error">{error}</p>}
                    <div className="al-beta-note">One email when it ships · no newsletter</div>
                  </div>
                </>
              ) : (
                <div className="al-beta-thanks">
                  <div className="al-beta-check">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <div className="al-beta-thanks-title">{t("reports_thanks_title")}</div>
                  <div className="al-beta-thanks-sub">{t("reports_thanks_sub")}</div>
                </div>
              )}
            </div>
          </div>

          <a className="al-btn al-btn-ghost al-reports-back" href="/">
            Back to Alister AI
          </a>
        </div>
      </section>
    </div>
  );
}
